/**
 * Currency formatting utility for ShadesParking Admin
 *
 * Supported currencies: USD, AED, EUR, PLN
 * All amounts are stored in smallest unit (cents/fils/grosze)
 */

export interface CurrencyConfig {
  code: string;
  symbol: string;
  position: 'before' | 'after';
  decimals: number;
  smallestUnit: number; // 100 for cents, 100 for grosze, etc.
}

export const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  USD: { code: 'USD', symbol: '$', position: 'before', decimals: 2, smallestUnit: 100 },
  AED: { code: 'AED', symbol: 'د.إ', position: 'after', decimals: 2, smallestUnit: 100 },
  EUR: { code: 'EUR', symbol: '€', position: 'before', decimals: 2, smallestUnit: 100 },
  PLN: { code: 'PLN', symbol: 'zł', position: 'after', decimals: 2, smallestUnit: 100 },
};

export const DEFAULT_CURRENCY = 'USD';

/**
 * Convert from smallest unit (cents) to display amount
 * Example: fromSmallestUnit(500, 'USD') => 5.00
 */
export function fromSmallestUnit(amount: number, currencyCode: string = DEFAULT_CURRENCY): number {
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
  return amount / config.smallestUnit;
}

/**
 * Convert from display amount to smallest unit (cents)
 * Example: toSmallestUnit(5.00, 'USD') => 500
 */
export function toSmallestUnit(amount: number, currencyCode: string = DEFAULT_CURRENCY): number {
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
  return Math.round(amount * config.smallestUnit);
}

export interface FormatCurrencyOptions {
  useSymbol?: boolean;  // Use symbol ($ / €) instead of code (USD / EUR)
  showDecimals?: boolean; // Show decimal places
}

/**
 * Format a currency amount for display
 * Example: formatCurrency(5.00, 'USD') => "USD5.00" or "$5.00" with useSymbol
 * Example: formatCurrency(5.00, 'AED') => "5.00 AED" or "5.00 د.إ" with useSymbol
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY,
  options: FormatCurrencyOptions = {}
): string {
  const { useSymbol = false, showDecimals = true } = options;
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG[DEFAULT_CURRENCY];

  const formatted = showDecimals
    ? amount.toFixed(config.decimals)
    : Math.round(amount).toString();

  const label = useSymbol ? config.symbol : config.code;

  if (config.position === 'before') {
    return `${label}${formatted}`;
  }
  return `${formatted} ${label}`;
}

/**
 * Format a rate with time unit
 * Example: formatRate(5.00, 'USD', 'hr') => "USD5.00/hr"
 */
export function formatRate(
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY,
  unit: string = 'hr'
): string {
  return `${formatCurrency(amount, currencyCode)}/${unit}`;
}

/**
 * Get currency configuration
 */
export function getCurrencyConfig(currencyCode: string): CurrencyConfig {
  return CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG[DEFAULT_CURRENCY];
}

/**
 * Get list of supported currency codes
 */
export function getSupportedCurrencies(): string[] {
  return Object.keys(CURRENCY_CONFIG);
}
